import React, { useState, useRef, useEffect } from 'react';
import { LayoutDashboard, LayoutGrid, PieChart, UserSearch, UsersRound, UserRoundSearch, Bookmark, Megaphone, CircleDollarSign, Store, Target, RefreshCw, PackageCheck, CircleUserRound, Smile, Activity, TrendingUp, Route } from 'lucide-react';
import { createPortal } from 'react-dom';
import { NavigationItem } from 'common/models/navigation';
import { PrimaryRoutes } from 'common/enums/internal-route-enums';
import { Page } from 'common/enums/page-enums';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFavourites } from '../../../../hooks/useFavourites';
import { isNavigationItemActive } from '../../../../common/helpers/main-menu-route-helper';

const ConversionIcon = ({ className }: { className?: string }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.75"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
	>
		<line x1="4" y1="7" x2="20" y2="7" />
		<line x1="7" y1="12" x2="17" y2="12" />
		<line x1="10" y1="17" x2="14" y2="17" />
	</svg>
);

const UserStar = ({ className }: { className?: string }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.75"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
	>
		<path d="M16.051 12.616a1 1 0 0 1 1.909.024l.737 1.452a1 1 0 0 0 .737.535l1.634.256a1 1 0 0 1 .588 1.806l-1.172 1.168a1 1 0 0 0-.282.866l.259 1.613a1 1 0 0 1-1.541 1.134l-1.465-.75a1 1 0 0 0-.912 0l-1.465.75a1 1 0 0 1-1.539-1.133l.258-1.613a1 1 0 0 0-.282-.866l-1.156-1.153a1 1 0 0 1 .572-1.822l1.633-.256a1 1 0 0 0 .737-.535z" />
		<path d="M8 15H7a4 4 0 0 0-4 4v2" />
		<circle cx="10" cy="7" r="4" />
	</svg>
);

interface NavigationProps {
  collapsed?: boolean;
  permittedPages?: string[];
  onClose?: () => void; 
}


export const Navigation: React.FC<NavigationProps> = ({ collapsed = false, permittedPages = [], onClose }) => {
	const [hoveredItem, setHoveredItem] = useState<string | null>(null);
	const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
	const [showFavouritesSubmenu, setShowFavouritesSubmenu] = useState(false);
	const [submenuPosition, setSubmenuPosition] = useState<React.CSSProperties>({});
	const navRef = useRef<HTMLDivElement>(null);
	const favouritesButtonRef = useRef<HTMLButtonElement>(null);
	const submenuRef = useRef<HTMLDivElement>(null);
	const navigate = useNavigate();
	const location = useLocation();
	
	// Favourites hook
	const { mostFavourites, loadMostFavourites, hasLoadedMostFavourites } = useFavourites();

	// Load favourites when component mounts and user has permission
	useEffect(() => {
		if (permittedPages.includes('Favourite') && !hasLoadedMostFavourites) {
			loadMostFavourites();
		}
	}, [permittedPages, hasLoadedMostFavourites, loadMostFavourites]);

	// Generate submenu items from favourites data
	const favouritesSubmenuItems = mostFavourites.map((favourite) => ({
		id: `favourite-${favourite.id}`,
		label: favourite.name,
		favourite
	}));

	const navigationItems: NavigationItem[] = [
		{
			id: 'dashboard',
			label: 'Dashboard',
			icon: 'layout-dashboard',
			routes: [PrimaryRoutes.Home],
			page: Page.Dashboard,
		},
		{
			id: 'sales-insights',
			label: 'Sales',
			icon: 'chart-pie',
			routes: [PrimaryRoutes.DataInsights],
			page: Page.DataInsights,
		},
		{
			id: 'ops-insights',
			label: 'Ops Insights',
			icon: 'layout-grid',
			routes: [PrimaryRoutes.OpsInsights],
			page: Page.OpsInsights,
		},
		{
			id: 'd2c-dashboard',
			label: 'D2C Dashboard',
			icon: 'layout-dashboard',
			routes: [PrimaryRoutes.D2CDashboard],
			page: Page.D2CDashboard,
		},
		{
			id: 'retail-dashboard',
			label: 'Retail Dashboard',
			icon: 'store',
			routes: [PrimaryRoutes.RetailDashboard],
			page: Page.RetailDashboard,
		},
		{
			id: 'acquisition',
			label: 'Acquisition',
			icon: 'target',
			routes: [PrimaryRoutes.Acquisition],
			page: Page.Acquisition,
		},
		{
			id: 'conversion-insights',
			label: 'Conversion',
			icon: 'conversion',
			routes: [PrimaryRoutes.ConversionInsights],
			page: Page.ConversionInsights,
		},
		{
			id: 'retention',
			label: 'Retention',
			icon: 'refresh-cw',
			routes: [PrimaryRoutes.Retention],
			page: Page.Retention,
		},
		{
			id: 'subscription',
			label: 'Subscription',
			icon: 'package-check',
			routes: [PrimaryRoutes.Subscription],
			page: Page.Subscription,
		},
		{
			id: 'customer',
			label: 'Customers',
			icon: 'circle-user-round',
			routes: [PrimaryRoutes.Customer],
			page: Page.Customer,
		},
		{
			id: 'product-insights',
			label: 'Products',
			icon: 'tag',
			routes: [PrimaryRoutes.ProductInsights],
			page: Page.ProductInsights,
		},
		{
			id: 'cx-insights',
			label: 'Experience',
			icon: 'smile',
			routes: [PrimaryRoutes.CXInsights],
			page: Page.CXInsights,
		},
		{
			id: 'crm-insights',
			label: 'CRM Insights',
			icon: 'megaphone',
			routes: [PrimaryRoutes.CRMInsights],
			page: Page.CRMInsights,
		},
		{
			id: 'campaigns',
			label: 'Campaigns',
			icon: 'megaphone',
			routes: [PrimaryRoutes.Campaigns],
			page: Page.Campaigns,
		},
		{
			id: 'profit-insights',
			label: 'Profit',
			icon: 'circle-dollar-sign',
			routes: [PrimaryRoutes.ProfitInsights],
			page: Page.ProfitInsights,
		},
		{
			id: 'growth-model',
			label: 'Growth Model',
			icon: 'trending-up',
			routes: [PrimaryRoutes.GrowthModel],
			page: Page.GrowthModel,
		},
		{
			id: 'journey-explorer',
			label: 'Journey Explorer',
			icon: 'route',
			routes: [PrimaryRoutes.JourneyExplorer],
			page: Page.JourneyExplorer,
		},
		{
			id: 'customer-segments',
			label: 'Customer Segments',
			icon: 'users-round',
			routes: [PrimaryRoutes.Segments],
			page: Page.Segments,
		},
		{
			id: 'customer-profile',
			label: 'Customer Profile',
			icon: 'user-round-search',
			routes: [PrimaryRoutes.Contacts],
			page: Page.Contacts,
		},
		{
			id: 'favourites',
			label: 'Favourites',
			icon: 'bookmark',
			routes: [],
			page: 'Favourite' as any,
		},
	];

	const aiSignalsItems: NavigationItem[] = [
		{
			id: 'cac-signals',
			label: 'CAC',
			icon: 'target',
			routes: [PrimaryRoutes.CACSignals],
			page: Page.CACSignals,
			comingSoon: true,
		},
		{
			id: 'ltv-signals',
			label: 'LTV',
			icon: 'refresh-cw',
			routes: [PrimaryRoutes.LTVSignals],
			page: Page.LTVSignals,
			comingSoon: true,
		},
		{
			id: 'conversion-signals',
			label: 'Conversion',
			icon: 'conversion',
			routes: [PrimaryRoutes.ConversionSignals],
			page: Page.ConversionSignals,
			comingSoon: true,
		},
		{
			id: 'performance-signals',
			label: 'Performance',
			icon: 'activity',
			routes: [PrimaryRoutes.PerformanceSignals],
			page: Page.PerformanceSignals,
		},
		{
			id: 'experience-signals',
			label: 'Experience',
			icon: 'smile',
			routes: [PrimaryRoutes.ExperienceSignals],
			page: Page.ExperienceSignals,
		},
	];

	const ICON_STROKE_WIDTH = "1.75";

	const getIcon = (iconName: string) => {
		// Common props for all icons
		const iconProps = {
			className: "w-[20px] h-[20px]",
			strokeWidth: ICON_STROKE_WIDTH
		};

		switch (iconName) {
			case 'layout-dashboard':
				return <LayoutDashboard {...iconProps} />;
			case 'chart-pie':
				return <PieChart {...iconProps} />;
			case 'user-search':
				return <UserSearch {...iconProps} />;
			case 'users-round':
				return <UsersRound {...iconProps} />;
			case 'user-round-search':
				return <UserRoundSearch {...iconProps} />;
			case 'megaphone':
				return <Megaphone {...iconProps} />;
			case 'circle-dollar-sign':
				return <CircleDollarSign {...iconProps} />;
			case 'trending-up':
				return <TrendingUp {...iconProps} />;
			case 'route':
				return <Route {...iconProps} />;
			case 'user-star':
				return <UserStar className="w-[20px] h-[20px]" />;
			case 'smile':
				return <Smile {...iconProps} />;
			case 'activity':
				return <Activity {...iconProps} />;
			case 'bookmark':
				return <Bookmark {...iconProps} />;
			case 'layout-grid':
				return <LayoutGrid {...iconProps} />;
			case 'store':
				return <Store {...iconProps} />;
			case 'target':
				return <Target {...iconProps} />;
			case 'conversion':
				return <ConversionIcon className="w-[20px] h-[20px]" />;
			case 'refresh-cw':
				return <RefreshCw {...iconProps} />;
			case 'package-check':
				return <PackageCheck {...iconProps} />;
			case 'circle-user-round':
				return <CircleUserRound {...iconProps} />;
			default:
				return <LayoutDashboard {...iconProps} />;
		}
	};

	const filteredNavigationsMenuItems = navigationItems.filter((item) => {
		if (!item.page) {
			return false;
		}
		const pageName = typeof item.page === 'string' ? item.page : (item.page as Page).toString();
		return permittedPages.includes(pageName);
	});

	const filteredAiSignalsItems = aiSignalsItems.filter((item) => {
		if (!item.page) {
			return false;
		}
		const pageName = typeof item.page === 'string' ? item.page : (item.page as Page).toString();
		return permittedPages.includes(pageName);
	});

	const showAiSignals = filteredAiSignalsItems.length > 0;

	const updateTooltipPosition = (event: React.MouseEvent) => {
		if (!collapsed) return;
		
		const rect = event.currentTarget.getBoundingClientRect();
		
		setTooltipStyle({
			position: 'absolute',
			left: rect.right + window.scrollX + 8,
			top: rect.top + window.scrollY + rect.height / 2,
			transform: 'translateY(-50%)',
			zIndex: 9999
		});
	};

	const updateSubmenuPosition = () => {
		if (!favouritesButtonRef.current) return;
		
		const rect = favouritesButtonRef.current.getBoundingClientRect();
		
		setSubmenuPosition({
			position: 'absolute',
			left: collapsed ? rect.right + window.scrollX + 8 : rect.left + window.scrollX,
			top: collapsed ? rect.top + window.scrollY : rect.bottom + window.scrollY + 4,
			zIndex: 9999
		});
	};

	const handleFavouritesClick = async () => {
		if (collapsed) return; // Don't show submenu when sidebar is collapsed
		
		// Load favourites if not already loaded
		if (mostFavourites.length === 0) {
			await loadMostFavourites();
		}
		
		updateSubmenuPosition();
		setShowFavouritesSubmenu(!showFavouritesSubmenu);
	};

	const handleMouseEnter = (item: NavigationItem, event: React.MouseEvent) => {
		if (collapsed && item.id !== 'favourites') {
			updateTooltipPosition(event);
			setHoveredItem(item.id);
		}
	};

	const handleMouseLeave = () => {
		setHoveredItem(null);
	};

	// Close submenu when sidebar becomes collapsed
	useEffect(() => {
		if (collapsed && showFavouritesSubmenu) {
			setShowFavouritesSubmenu(false);
			onClose?.();
		}
	}, [collapsed, showFavouritesSubmenu]);

	// Close submenu when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				submenuRef.current && 
				!submenuRef.current.contains(event.target as Node) &&
				favouritesButtonRef.current &&
				!favouritesButtonRef.current.contains(event.target as Node)
			) {
				setShowFavouritesSubmenu(false);
				onClose?.();
			}
		};

		if (showFavouritesSubmenu) {
			document.addEventListener('mousedown', handleClickOutside);
			return () => document.removeEventListener('mousedown', handleClickOutside);
		}
	}, [showFavouritesSubmenu]);

	useEffect(() => {
		if (hoveredItem && collapsed) {
			const handler = () => setHoveredItem(null);
			window.addEventListener('scroll', handler);
			window.addEventListener('resize', handler);
			return () => {
				window.removeEventListener('scroll', handler);
				window.removeEventListener('resize', handler);
			};
		}
	}, [hoveredItem, collapsed]);

	const tooltipElement = collapsed && hoveredItem ? (
		<div style={tooltipStyle} className="pointer-events-none absolute z-20 transition-opacity duration-200">
			<div className="flex items-center flex-row">
				<div className="w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-gray-800" style={{ marginRight: '-1px' }} />
				<div className="rounded bg-gray-800 p-2 text-center text-caption text-white whitespace-nowrap">
					{[...navigationItems, ...aiSignalsItems].find((item) => item.id === hoveredItem)?.label}
				</div>
			</div>
		</div>
	) : null;

	const handleFavouriteClick = (favourite: any) => {
		if (favourite.link) {
			// Check if it's a Power BI report URL with page parameter
			if (favourite.link.includes('?page=')) {
				const [pathPart, queryPart] = favourite.link.split('?');
				const urlParams = new URLSearchParams(queryPart || '');
				const pageId = urlParams.get('page');

				// Normalize the target path
				let targetPath = pathPart.startsWith('/') ? pathPart : `/${pathPart}`;
				
				if (pageId) {
					// Check if we're already on the target page (same base path)
					const currentPath = window.location.pathname;
					
					if (currentPath === targetPath) {
						// Same page, just update the page parameter and dispatch event
						const newUrl = `${targetPath}?page=${pageId}`;
						window.history.pushState({}, '', newUrl);
						
						// Dispatch event to navigate within the same Power BI report
						window.dispatchEvent(new CustomEvent('powerbi-navigate', { 
							detail: { pageId: pageId } 
						}));
					} else {
						// Different page, use React Router navigation
						const newUrl = `${targetPath}?page=${pageId}`;
						navigate(newUrl);
					}
				}
			} else {
				// Handle regular navigation for non-Power BI URLs
				let navigationPath = favourite.link;
				
				// If it's a full URL, extract the pathname
				if (favourite.link.startsWith('http://') || favourite.link.startsWith('https://')) {
					try {
						const url = new URL(favourite.link);
						navigationPath = url.pathname;
					} catch (error) {
						console.error('Invalid URL format:', favourite.link);
						return;
					}
				}
				
				// Ensure the path starts with /
				if (!navigationPath.startsWith('/')) {
					navigationPath = '/' + navigationPath;
				}
				
				// Navigate within the application
				navigate(navigationPath);
			}
		}
		setShowFavouritesSubmenu(false);
		onClose?.();
	};

	const submenuElement = favouritesSubmenuItems.length > 0 ? (
		<div 
			ref={submenuRef}
			style={submenuPosition} 
			className={`absolute z-50 ml-6 py-2 min-w-[180px] transition-all duration-200 ease-in-out origin-top ${
				showFavouritesSubmenu 
					? 'opacity-100 scale-y-100 pointer-events-auto' 
					: 'opacity-0 scale-y-0 pointer-events-none'
			}`}
		>
			{favouritesSubmenuItems.map((submenuItem) => (
				<button
					key={submenuItem.id}
					onClick={() => handleFavouriteClick(submenuItem.favourite)}
					className="w-full flex items-center px-4 py-2 text-caption rounded-full text-primary-text hover:bg-tertiary-background transition-colors duration-200"
				>
					{submenuItem.label}
				</button>
			))}
		</div>
	) : null;

	const onNavigationHandler = (page: Page) => {
		switch (page) {
			case Page.Dashboard:
				navigate(PrimaryRoutes.Home);
				break;
			case Page.Contacts:
				navigate(PrimaryRoutes.Contacts);
				break;
			case Page.Segments:
				navigate(PrimaryRoutes.Segments);
				break;
			case Page.Campaigns:
				navigate(PrimaryRoutes.Campaigns);
				break;
			case Page.CXInsights:
				navigate(PrimaryRoutes.CXInsights);
				break;
			case Page.DataInsights:
				navigate(PrimaryRoutes.DataInsights);
				break;
			case Page.OpsInsights:
				navigate(PrimaryRoutes.OpsInsights);
				break;
			case Page.ProfitInsights:
				navigate(PrimaryRoutes.ProfitInsights);
				break;
			case Page.CRMInsights:
				navigate(PrimaryRoutes.CRMInsights);
				break;
			case Page.Settings:
				navigate(PrimaryRoutes.Settings);
				break;
			case Page.D2CDashboard:
				navigate(PrimaryRoutes.D2CDashboard);
				break;
			case Page.RetailDashboard:
				navigate(PrimaryRoutes.RetailDashboard);
				break;
			case Page.ConversionInsights:
				navigate(PrimaryRoutes.ConversionInsights);
				break;
			case Page.ProductInsights:
				navigate(PrimaryRoutes.ProductInsights);
				break;
			case Page.Acquisition:
				navigate(PrimaryRoutes.Acquisition);
				break;
			case Page.Retention:
				navigate(PrimaryRoutes.Retention);
				break;
			case Page.Subscription:
				navigate(PrimaryRoutes.Subscription);
				break;
			case Page.Customer:
				navigate(PrimaryRoutes.Customer);
				break;
			case Page.CACSignals:
				navigate(PrimaryRoutes.CACSignals);
				break;
			case Page.LTVSignals:
				navigate(PrimaryRoutes.LTVSignals);
				break;
			case Page.ConversionSignals:
				navigate(PrimaryRoutes.ConversionSignals);
				break;
			case Page.ExperienceSignals:
				navigate(PrimaryRoutes.ExperienceSignals);
				break;
			case Page.PerformanceSignals:
				navigate(PrimaryRoutes.PerformanceSignals);
				break;
			case Page.GrowthModel:
				navigate(PrimaryRoutes.GrowthModel);
				break;
			case Page.JourneyExplorer:
				navigate(PrimaryRoutes.JourneyExplorer);
				break;
		}

		onClose?.();
	};

	const isNavItemActive = (item: NavigationItem) => {
		if (!item.page || !item.routes?.length) {
			return false;
		}
		return isNavigationItemActive(location.pathname, item.page, item.routes, permittedPages);
	};

	const renderNavButton = (item: NavigationItem, index: number, keyPrefix: string) => (
		<button
			key={`${keyPrefix}-${index}`}
			ref={item.id === 'favourites' ? favouritesButtonRef : null}
			onClick={() => item.id === 'favourites' ? handleFavouritesClick() : onNavigationHandler(item.page!)}
			onMouseEnter={(e) => handleMouseEnter(item, e)}
			onMouseLeave={handleMouseLeave}
			className={`group relative flex items-center transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)]
                ${
					collapsed
						? 'w-9 h-9 rounded-[40px] justify-center'
						: 'w-[206px] h-9 px-2 rounded-[40px] justify-start'
				}
                ${
					isNavItemActive(item) || (item.id === 'favourites' && showFavouritesSubmenu)
						? 'bg-secondary-background text-primary shadow-[inset_0_0_0_0.2px_theme(colors.tertiary-background)]'
						: 'text-primary-text hover:bg-tertiary-background'
				}
              `}
		>
			<div className="w-6 h-6 flex items-center justify-center shrink-0">
				<span
					className={`transition-colors duration-500 ${
						isNavItemActive(item) || (item.id === 'favourites' && showFavouritesSubmenu)
							? 'text-primary'
							: 'text-primary-text'
					}`}
				>
					{getIcon(item.icon)}
				</span>
			</div>

			{!collapsed && (
				<div
					className={`overflow-hidden whitespace-nowrap transition-[max-width,opacity,margin] duration-200 ease-[cubic-bezier(0.4,0,0.2,1)]
                  ${collapsed ? 'max-w-0 opacity-0 m-0' : 'max-w-[160px] opacity-100 ml-[10px]'}
                `}
					style={{ willChange: 'max-width, opacity, margin' }}
				>
					<span className="text-ui">{item.label}</span>
				</div>
			)}
			{!collapsed && item.comingSoon ? (
				<span
					className="ml-auto w-[5px] h-[5px] rounded-full bg-[#555] shrink-0"
					aria-hidden="true"
				/>
			) : null}
		</button>
	);

	return (
		<>
			<div ref={navRef} className={`flex-1 py-3 ${collapsed ? 'px-3' : 'px-3'}`}>
				<nav className={`flex flex-col ${collapsed ? 'space-y-2 items-center' : 'space-y-1 items-stretch'}`}>
					{filteredNavigationsMenuItems.map((item, index) => renderNavButton(item, index, 'navigation-item'))}

					{showAiSignals && (
						<div
							role="group"
							{...(collapsed
								? { 'aria-label': 'AI Signals' }
								: { 'aria-labelledby': 'sidebar-ai-signals-heading' })}
							className={`flex flex-col ${collapsed ? 'space-y-2 items-center' : 'space-y-1 items-stretch'}`}
						>
							<div
								className={`border-t border-tertiary-background ${collapsed ? 'w-6 my-2' : 'w-full mt-3 mb-2'}`}
								aria-hidden="true"
							/>
							{!collapsed && (
								<p
									id="sidebar-ai-signals-heading"
									className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-secondary-text"
								>
									AI Signals
								</p>
							)}
							{filteredAiSignalsItems.map((item, index) => renderNavButton(item, index, 'ai-signals-item'))}
						</div>
					)}
				</nav>
			</div>

			{tooltipElement && createPortal(tooltipElement, document.body)}
			{submenuElement && createPortal(submenuElement, document.body)}
		</>
	);
};